/* eslint-disable no-console */
import * as signalR from '@microsoft/signalr';
import { ApiDomainUrl } from './utils/Common';

/**
 * InterpreterMessagingClient - SignalR client wrapper for real-time messaging
 *
 * Features:
 * - Automatic reconnection with exponential backoff
 * - Event subscription for NewMessage, UserTyping, MessageRead, UserPresence, Error
 * - Client-to-server methods: sendMessage, startTyping, stopTyping, markAsRead, joinConversation, leaveConversation
 * - Connection state management and callbacks
 */
export class InterpreterMessagingClient {
  constructor(token) {
    // Use app's configured API domain for SignalR hub URL
    this.hubUrl = `${ApiDomainUrl()}/hubs/messaging`;
    this.token = token;
    this.connection = null;
    this.connectionState = 'disconnected';
    this.reconnectAttempts = 0;
    this.maxReconnectAttempts = 10;
    this.reconnectTimer = null;
    this.joinedConversations = new Set();

    // Callback storage
    this.callbacks = {
      onNewMessage: [],
      onTyping: [],
      onReadReceipt: [],
      onPresence: [],
      onError: [],
      onConnectionChange: [],
    };
  }

  /**
   * Connect to SignalR hub with retry logic
   */
  async connect() {
    if (this.connection && this.connectionState === 'connected') {
      console.log('InterpreterMessagingClient: Already connected');
      return;
    }

    try {
      this.setConnectionState('connecting');

      // Build connection
      this.connection = new signalR.HubConnectionBuilder()
        .withUrl(this.hubUrl, {
          accessTokenFactory: () => this.token,
        })
        .withAutomaticReconnect({
          nextRetryDelayInMilliseconds: (retryContext) => {
            // Exponential backoff: 1s, 2s, 4s, 8s, 16s, max 30s
            const delays = [1000, 2000, 4000, 8000, 16000, 30000];
            return delays[Math.min(retryContext.previousRetryCount, delays.length - 1)];
          },
        })
        .configureLogging(signalR.LogLevel.Information)
        .build();

      // Register event handlers
      this.registerEventHandlers();

      // Register connection lifecycle handlers
      this.connection.onreconnecting((error) => {
        console.log('InterpreterMessagingClient: Reconnecting...', error);
        this.setConnectionState('reconnecting');
      });

      this.connection.onreconnected((connectionId) => {
        console.log('InterpreterMessagingClient: Reconnected', connectionId);
        this.reconnectAttempts = 0;
        this.setConnectionState('connected');

        // Re-join all conversations
        this.rejoinConversations();
      });

      this.connection.onclose((error) => {
        console.log('InterpreterMessagingClient: Connection closed', error);
        this.setConnectionState('disconnected');

        // Attempt manual reconnection if not already reconnecting
        if (this.reconnectAttempts < this.maxReconnectAttempts) {
          this.scheduleReconnect();
        } else {
          this.emitError({
            code: 'MAX_RECONNECT_ATTEMPTS',
            message: 'Maximum reconnection attempts reached',
          });
        }
      });

      // Start connection
      await this.connection.start();
      console.log('InterpreterMessagingClient: Connected');
      this.reconnectAttempts = 0;
      this.setConnectionState('connected');

    } catch (error) {
      console.error('InterpreterMessagingClient: Connection failed', error);
      this.setConnectionState('error');

      // Classify and emit error
      const classifiedError = this.classifyError(error);
      this.emitError(classifiedError);

      // Don't auto-reconnect on auth errors - user needs to re-authenticate
      if (classifiedError.code === 'AUTH_EXPIRED' || classifiedError.code === 'AUTH_FORBIDDEN') {
        console.error('InterpreterMessagingClient: Auth error, not auto-reconnecting');
        return;
      }

      // Schedule reconnect for other error types
      this.scheduleReconnect();
    }
  }

  /**
   * Disconnect from SignalR hub
   */
  async disconnect() {
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }

    if (this.connection) {
      try {
        await this.connection.stop();
        console.log('InterpreterMessagingClient: Disconnected');
      } catch (error) {
        console.error('InterpreterMessagingClient: Disconnect error', error);
      }
    }

    this.connection = null;
    this.joinedConversations.clear();
    this.setConnectionState('disconnected');
  }

  /**
   * Schedule reconnection with exponential backoff
   */
  scheduleReconnect() {
    if (this.reconnectTimer) {
      return; // Already scheduled
    }

    if (this.reconnectAttempts >= this.maxReconnectAttempts) {
      console.error('InterpreterMessagingClient: Max reconnect attempts reached');
      this.setConnectionState('error');
      return;
    }

    // Exponential backoff: 1s, 2s, 4s, 8s, 16s, max 30s
    const delays = [1000, 2000, 4000, 8000, 16000, 30000];
    const delay = delays[Math.min(this.reconnectAttempts, delays.length - 1)];

    console.log(`InterpreterMessagingClient: Reconnecting in ${delay}ms (attempt ${this.reconnectAttempts + 1}/${this.maxReconnectAttempts})`);

    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = null;
      this.reconnectAttempts++;
      this.connect();
    }, delay);
  }

  /**
   * Re-join all conversations after reconnection
   */
  async rejoinConversations() {
    console.log('InterpreterMessagingClient: Re-joining conversations', this.joinedConversations);

    const conversationsToRejoin = Array.from(this.joinedConversations);

    for (const conversationId of conversationsToRejoin) {
      try {
        await this.joinConversation(conversationId);
      } catch (error) {
        console.error('InterpreterMessagingClient: Failed to rejoin conversation', conversationId, error);
      }
    }
  }

  /**
   * Register all SignalR event handlers
   */
  registerEventHandlers() {
    if (!this.connection) {
      return;
    }

    // NewMessage event
    this.connection.on('NewMessage', (message) => {
      console.log('InterpreterMessagingClient: NewMessage', message);

      try {
        this.callbacks.onNewMessage.forEach(callback => {
          try {
            callback(message);
          } catch (error) {
            console.error('InterpreterMessagingClient: NewMessage callback error', error);
          }
        });
      } catch (error) {
        console.error('InterpreterMessagingClient: NewMessage handler error', error);
      }
    });

    // UserTyping event
    this.connection.on('UserTyping', (update) => {
      console.log('InterpreterMessagingClient: UserTyping', update);

      try {
        this.callbacks.onTyping.forEach(callback => {
          try {
            callback(update);
          } catch (error) {
            console.error('InterpreterMessagingClient: UserTyping callback error', error);
          }
        });
      } catch (error) {
        console.error('InterpreterMessagingClient: UserTyping handler error', error);
      }
    });

    // MessageRead event
    this.connection.on('MessageRead', (receipt) => {
      console.log('InterpreterMessagingClient: MessageRead', receipt);

      try {
        this.callbacks.onReadReceipt.forEach(callback => {
          try {
            callback(receipt);
          } catch (error) {
            console.error('InterpreterMessagingClient: MessageRead callback error', error);
          }
        });
      } catch (error) {
        console.error('InterpreterMessagingClient: MessageRead handler error', error);
      }
    });

    // UserPresence event
    this.connection.on('UserPresence', (presence) => {
      console.log('InterpreterMessagingClient: UserPresence', presence);

      try {
        this.callbacks.onPresence.forEach(callback => {
          try {
            callback(presence);
          } catch (error) {
            console.error('InterpreterMessagingClient: UserPresence callback error', error);
          }
        });
      } catch (error) {
        console.error('InterpreterMessagingClient: UserPresence handler error', error);
      }
    });

    // Error event
    this.connection.on('Error', (errorData) => {
      console.error('InterpreterMessagingClient: Server error', errorData);
      this.emitError(errorData);
    });
  }

  /**
   * Set connection state and notify callbacks
   */
  setConnectionState(state) {
    if (this.connectionState === state) {
      return;
    }

    this.connectionState = state;
    console.log('InterpreterMessagingClient: Connection state changed to', state);

    this.callbacks.onConnectionChange.forEach(callback => {
      try {
        callback(state);
      } catch (error) {
        console.error('InterpreterMessagingClient: Connection change callback error', error);
      }
    });
  }

  /**
   * Emit error to all error callbacks
   */
  emitError(errorData) {
    this.callbacks.onError.forEach(callback => {
      try {
        callback(errorData);
      } catch (error) {
        console.error('InterpreterMessagingClient: Error callback error', error);
      }
    });
  }

  /**
   * Classify error type based on error object
   *
   * Error Types:
   * - AUTH_EXPIRED: 401 errors, token expired, requires re-authentication
   * - AUTH_FORBIDDEN: 403 errors, insufficient permissions
   * - NETWORK_ERROR: Network connectivity issues
   * - TIMEOUT_ERROR: Request timeout
   * - SERVER_ERROR: 500 errors, server-side issues
   * - CONNECTION_ERROR: WebSocket/SignalR connection issues
   * - UNKNOWN_ERROR: Unclassified errors
   *
   * @param {Error} error - The error object to classify
   * @returns {Object} Classified error with code and user-friendly message
   */
  classifyError(error) {
    if (!error) {
      return { code: 'UNKNOWN_ERROR', message: 'An unknown error occurred' };
    }

    // Check for authentication/authorization errors
    if (error.statusCode === 401 || error.message?.includes('401') || error.message?.includes('Unauthorized')) {
      return { code: 'AUTH_EXPIRED', message: 'Authentication expired. Please log in again.' };
    }

    if (error.statusCode === 403 || error.message?.includes('403') || error.message?.includes('Forbidden')) {
      return { code: 'AUTH_FORBIDDEN', message: 'You do not have permission to access this resource.' };
    }

    // Check for network errors
    if (error.name === 'TypeError' && error.message?.includes('fetch')) {
      return { code: 'NETWORK_ERROR', message: 'Unable to connect. Check your internet connection.' };
    }

    if (error.message?.includes('timeout') || error.message?.includes('timed out')) {
      return { code: 'TIMEOUT_ERROR', message: 'Request timed out. Please try again.' };
    }

    // Check for server errors
    if (error.statusCode >= 500 || error.message?.includes('500') || error.message?.includes('Internal Server Error')) {
      return { code: 'SERVER_ERROR', message: 'Something went wrong on the server. Please try again later.' };
    }

    // Connection-specific errors
    if (error.message?.includes('connection') || error.message?.includes('WebSocket')) {
      return { code: 'CONNECTION_ERROR', message: 'Unable to establish connection. Please check your network.' };
    }

    // Default
    return {
      code: 'UNKNOWN_ERROR',
      message: error.message || 'An unexpected error occurred',
    };
  }

  /**
   * Check if error requires re-authentication
   * @param {Object} classifiedError - Error from classifyError()
   * @returns {boolean} True if user needs to re-authenticate
   */
  requiresReauthentication(classifiedError) {
    return classifiedError.code === 'AUTH_EXPIRED' || classifiedError.code === 'AUTH_FORBIDDEN';
  }

  /**
   * Check if error is retryable (transient)
   * @param {Object} classifiedError - Error from classifyError()
   * @returns {boolean} True if error is transient and can be retried
   */
  isRetryableError(classifiedError) {
    const retryableCodes = ['NETWORK_ERROR', 'TIMEOUT_ERROR', 'CONNECTION_ERROR', 'SERVER_ERROR'];
    return retryableCodes.includes(classifiedError.code);
  }

  // ========================================
  // Event Subscription Methods
  // ========================================

  /**
   * Subscribe to new messages
   * @returns {Function} Unsubscribe function
   */
  onNewMessage(callback) {
    this.callbacks.onNewMessage.push(callback);
    return () => {
      const index = this.callbacks.onNewMessage.indexOf(callback);
      if (index > -1) {
        this.callbacks.onNewMessage.splice(index, 1);
      }
    };
  }

  /**
   * Subscribe to typing updates
   * @returns {Function} Unsubscribe function
   */
  onTyping(callback) {
    this.callbacks.onTyping.push(callback);
    return () => {
      const index = this.callbacks.onTyping.indexOf(callback);
      if (index > -1) {
        this.callbacks.onTyping.splice(index, 1);
      }
    };
  }

  /**
   * Subscribe to read receipts
   * @returns {Function} Unsubscribe function
   */
  onReadReceipt(callback) {
    this.callbacks.onReadReceipt.push(callback);
    return () => {
      const index = this.callbacks.onReadReceipt.indexOf(callback);
      if (index > -1) {
        this.callbacks.onReadReceipt.splice(index, 1);
      }
    };
  }

  /**
   * Subscribe to user presence updates
   * @returns {Function} Unsubscribe function
   */
  onPresence(callback) {
    this.callbacks.onPresence.push(callback);
    return () => {
      const index = this.callbacks.onPresence.indexOf(callback);
      if (index > -1) {
        this.callbacks.onPresence.splice(index, 1);
      }
    };
  }

  /**
   * Subscribe to errors
   * @returns {Function} Unsubscribe function
   */
  onError(callback) {
    this.callbacks.onError.push(callback);
    return () => {
      const index = this.callbacks.onError.indexOf(callback);
      if (index > -1) {
        this.callbacks.onError.splice(index, 1);
      }
    };
  }

  /**
   * Subscribe to connection state changes
   * @returns {Function} Unsubscribe function
   */
  onConnectionChange(callback) {
    this.callbacks.onConnectionChange.push(callback);
    return () => {
      const index = this.callbacks.onConnectionChange.indexOf(callback);
      if (index > -1) {
        this.callbacks.onConnectionChange.splice(index, 1);
      }
    };
  }

  // ========================================
  // Client-to-Server Methods
  // ========================================

  /**
   * Send a message
   */
  async sendMessage(conversationId, text) {
    if (!this.connection || this.connectionState !== 'connected') {
      const error = new Error('Not connected to messaging hub');
      error.code = 'NOT_CONNECTED';
      throw error;
    }

    try {
      await this.invokeWithTimeout('SendMessage', conversationId, text);
      console.log('InterpreterMessagingClient: Message sent', { conversationId, text });
    } catch (error) {
      console.error('InterpreterMessagingClient: SendMessage failed', error);

      // Classify error and emit to error listeners
      const classifiedError = this.classifyError(error);
      this.emitError({
        ...classifiedError,
        context: 'sendMessage',
        conversationId,
      });

      throw error;
    }
  }

  /**
   * Start typing indicator
   */
  async startTyping(conversationId) {
    if (!this.connection || this.connectionState !== 'connected') {
      return; // Typing indicators are non-critical, fail silently
    }

    try {
      await this.invokeWithTimeout('StartTyping', conversationId);
      console.log('InterpreterMessagingClient: StartTyping', conversationId);
    } catch (error) {
      console.error('InterpreterMessagingClient: StartTyping failed', error);
    }
  }

  /**
   * Stop typing indicator
   */
  async stopTyping(conversationId) {
    if (!this.connection || this.connectionState !== 'connected') {
      return; // Typing indicators are non-critical, fail silently
    }

    try {
      await this.invokeWithTimeout('StopTyping', conversationId);
      console.log('InterpreterMessagingClient: StopTyping', conversationId);
    } catch (error) {
      console.error('InterpreterMessagingClient: StopTyping failed', error);
    }
  }

  /**
   * Mark messages as read
   */
  async markAsRead(conversationId, messageId) {
    if (!this.connection || this.connectionState !== 'connected') {
      return; // Read receipts are non-critical, fail silently
    }

    try {
      await this.invokeWithTimeout('MarkAsRead', conversationId, messageId);
      console.log('InterpreterMessagingClient: MarkAsRead', { conversationId, messageId });
    } catch (error) {
      console.error('InterpreterMessagingClient: MarkAsRead failed', error);
    }
  }

  /**
   * Join a conversation
   */
  async joinConversation(conversationId) {
    if (!this.connection || this.connectionState !== 'connected') {
      const error = new Error('Not connected to messaging hub');
      error.code = 'NOT_CONNECTED';
      throw error;
    }

    try {
      await this.invokeWithTimeout('JoinConversation', conversationId);
      this.joinedConversations.add(conversationId);
      console.log('InterpreterMessagingClient: Joined conversation', conversationId);
    } catch (error) {
      console.error('InterpreterMessagingClient: JoinConversation failed', error);

      // Classify error and emit to error listeners
      const classifiedError = this.classifyError(error);
      this.emitError({
        ...classifiedError,
        context: 'joinConversation',
        conversationId,
      });

      throw error;
    }
  }

  /**
   * Leave a conversation
   */
  async leaveConversation(conversationId) {
    if (!this.connection || this.connectionState !== 'connected') {
      return; // Non-critical, fail silently
    }

    try {
      await this.invokeWithTimeout('LeaveConversation', conversationId);
      this.joinedConversations.delete(conversationId);
      console.log('InterpreterMessagingClient: Left conversation', conversationId);
    } catch (error) {
      console.error('InterpreterMessagingClient: LeaveConversation failed', error);
    }
  }

  /**
   * Ping the hub (keepalive)
   */
  async ping() {
    if (!this.connection || this.connectionState !== 'connected') {
      return;
    }

    try {
      await this.invokeWithTimeout('Ping');
      console.log('InterpreterMessagingClient: Ping successful');
    } catch (error) {
      console.error('InterpreterMessagingClient: Ping failed', error);
    }
  }

  /**
   * Invoke a hub method with timeout
   */
  async invokeWithTimeout(methodName, ...args) {
    const timeout = 10000; // 10 seconds

    return new Promise((resolve, reject) => {
      const timer = setTimeout(() => {
        reject(new Error(`Hub method '${methodName}' timed out after ${timeout}ms`));
      }, timeout);

      this.connection.invoke(methodName, ...args)
        .then((result) => {
          clearTimeout(timer);
          resolve(result);
        })
        .catch((error) => {
          clearTimeout(timer);
          reject(error);
        });
    });
  }

  /**
   * Get current connection state
   */
  getConnectionState() {
    return this.connectionState;
  }

  /**
   * Check if connected
   */
  isConnected() {
    return this.connectionState === 'connected';
  }
}
